import palette from "assets/theme/base/palette";
import React, { useState, useEffect } from "react";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import { call } from "services/axiosService/core";
import * as Sentry from "@sentry/browser";
import MaxuLogo from "assets/images/nav-logo.png";
import BotLogo from "assets/images/bot-logo.png";
import "./Bot.css";
import { useTranslation } from "react-i18next";

const TypingDots = () => (
  <div className="typing-indicator">
    <div className="typing-dot" />
    <div className="typing-dot" />
    <div className="typing-dot" />
  </div>
);

const Bot = ({
  showUnreadChat,
  setShowUnreadChat,
  showTodayContent,
  todayContent,
  chatQuestions,
  showIntro,
  userMetricsData,
  setUserMetricsData,
  autoExpandBot,
  profile,
}) => {
  const { t } = useTranslation();
  const [savedContent, setSavedContent] = useState(
    userMetricsData?.savedcontent?.length > 0 ? userMetricsData.savedcontent : []
  );
  const [expanded, setExpanded] = useState(autoExpandBot);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showThankYou, setShowThankYou] = useState(false);
  const [userInput, setUserInput] = useState("");
  const [showTyping, setShowTyping] = useState(true);
  const [showContent, setShowContent] = useState(showTodayContent);
  const [content, setContent] = useState(todayContent);
  const [questions, setQuestions] = useState([...chatQuestions]);
  const [showIntroductoryMessage, setShowIntroductoryMessage] = useState(showIntro);

  useEffect(() => {
    let timer;
    if (showTyping) {
      timer = setTimeout(() => setShowTyping(false), 2500);
    }
    return () => clearTimeout(timer);
  }, [showTyping, currentQuestionIndex]);

  const toggleChatbot = () => {
    if (showUnreadChat) {
      localStorage.setItem("lastBotViewed", new Date().toLocaleDateString());
      setShowUnreadChat(false);
    }
    if (showIntroductoryMessage) {
      localStorage.setItem("IntroMessageViewed", new Date().toLocaleDateString());
      setShowIntroductoryMessage(false);
    }
    setExpanded(!expanded);
  };

  const updateChatData = (data, other) => {
    data.other_responses.push(other);
    delete data.userResponse;
    call(`chats/${data.id}`, "PUT", { body: data })
      .then((res) => {
        if (res.status !== 200) console.log("error while updating other response");
      })
      .catch((err) => Sentry.captureException(err));
  };

  const getDailyContent = () => {
    window.pendo.track("Getting Maxwell daily recommendations");
    const data = questions.map((item) => ({
      question: item.question,
      userResponse: item.userResponse,
    }));
    call(`chats/${profile.id}/content`, "POST", { body: data })
      .then((res) => {
        if (res.status === 200) {
          localStorage.setItem("lastContentUpdatedDate", new Date().toLocaleDateString());
          localStorage.setItem("dailyContent", JSON.stringify(res.data));
          setShowContent(true);
          setContent([...res.data]);
        } else {
          console.log("error while getting daily content");
        }
      })
      .catch((err) => Sentry.captureException(err));
  };

  const handleAnswerSelection = (answer) => {
    if (answer === "Other") {
      const updatedQuestions = [...questions];
      updatedQuestions[currentQuestionIndex].userResponse = "Other";
      setQuestions(updatedQuestions);
    } else {
      setShowTyping(true);
      const updatedQuestions = [...questions];
      updatedQuestions[currentQuestionIndex].userResponse = answer;
      setQuestions(updatedQuestions);
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        getDailyContent();
        setShowThankYou(true);
      }
    }
  };

  const handleInputChange = (e) => setUserInput(e.target.value);

  const handleSubmit = () => {
    if (!userInput.trim()) return;
    updateChatData(questions[currentQuestionIndex], userInput);
    setShowTyping(true);
    const updatedQuestions = [...questions];
    updatedQuestions[currentQuestionIndex].userResponse = userInput;
    setQuestions(updatedQuestions);
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      getDailyContent();
      setShowThankYou(true);
    }
    setUserInput("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  const saveHandler = (title) => {
    let eventkey = { event: "savedcontent", value: title };
    const currentSavedContent = [...savedContent];
    const metricData = userMetricsData;
    if (currentSavedContent.includes(title)) {
      const index = currentSavedContent.indexOf(title);
      currentSavedContent.splice(index, 1);
      eventkey = { event: "removeSaved", value: title };
    } else {
      currentSavedContent.push(title);
    }
    metricData["savedcontent"] = [...currentSavedContent];
    metricData["eventkey"] = eventkey;
    call(`users/${profile.id}/contentmetrics`, "PUT", { body: metricData })
      .then((res) => {
        if (res.status === 200) {
          setSavedContent([...currentSavedContent]);
          setUserMetricsData(metricData);
        }
      })
      .catch((err) => {
        console.log(err);
        Sentry.captureException(err);
      });
  };

  const contentViewedHandler = (title) => {
    const viewedcontent = [...userMetricsData.viewedcontent];
    userMetricsData["viewedcontent"] = viewedcontent.includes(title)
      ? viewedcontent
      : [...viewedcontent, title];
    userMetricsData["eventkey"] = { event: "viewcontent", value: title };
    call(`users/${profile.id}/contentmetrics`, "PUT", { body: userMetricsData })
      .then((res) => {
        if (res.status === 200) setUserMetricsData(userMetricsData);
      })
      .catch((err) => {
        console.log(err);
        Sentry.captureException(err);
      });
  };

  const closeIntroductionaryMessage = () => {
    localStorage.setItem("IntroMessageViewed", new Date().toLocaleDateString());
    setShowIntroductoryMessage(false);
  };

  if (expanded) {
    return (
      <div className="chatbot-container">
        {/* Header */}
        <div className="chatbot-header" onClick={toggleChatbot}>
          <img src={BotLogo} className="chatbot-header-avatar" alt="Maxwell" />
          <div className="chatbot-header-info">
            <div className="chatbot-header-name">Maxwell</div>
            <div className="chatbot-header-status">
              <span className="chatbot-header-status-dot" />
              Online
            </div>
          </div>
          <IconButton className="chatbot-header-close" size="small" onClick={toggleChatbot}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Q&A flow */}
        {!showContent && (
          <div className="chatbot-body">
            {/* Welcome messages */}
            <div className="chat">
              <div className="chatbot-message">{t("bot.intro")}</div>
              <div className="chatbot-message">{t("bot.chatWithUs")}</div>
            </div>

            {questions.slice(0, currentQuestionIndex + 1).map((question, index) => (
              <div key={index} className={`chat ${question.userResponse ? "answered" : ""}`}>
                {!question.userResponse && (
                  <div className="question-container">
                    {showTyping ? (
                      <TypingDots />
                    ) : (
                      <>
                        <div className="chatbot-message">{question.question}</div>
                        <div className="options">
                          {question.options.map((option, optionIndex) => (
                            <div
                              key={optionIndex}
                              className="option"
                              onClick={() => handleAnswerSelection(option)}
                            >
                              {option}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {question.userResponse === "Other" && (
                  <>
                    <div className="answered-container">
                      <div className="chatbot-message">{question.question}</div>
                    </div>
                    <div className="user-input">
                      <input
                        type="text"
                        placeholder="Type your answer…"
                        value={userInput}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                      />
                      <button type="button" className="submit-button" onClick={handleSubmit}>
                        <SendIcon />
                      </button>
                    </div>
                  </>
                )}

                {question.userResponse && question.userResponse !== "Other" && (
                  <div className="answered-container">
                    <div className="chatbot-message">{question.question}</div>
                    <div className="user-message">{question.userResponse}</div>
                  </div>
                )}
              </div>
            ))}

            {showThankYou && (
              <div className="chat">
                <div className="chatbot-message">{t("bot.thanks")}</div>
                <div className="chatbot-message">{t("bot.findingContent")}</div>
              </div>
            )}
          </div>
        )}

        {/* Content cards */}
        {showContent && (
          <div className="chatbot-body">
            <div className="chat">
              <div className="chatbot-message">{t("bot.topContent")}</div>
            </div>

            {content.map((item, index) => {
              const saved = savedContent.includes(item.title);
              return (
                <div className="content-container" key={index}>
                  <div className="content-message">
                    <a
                      href={item.content_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => contentViewedHandler(item.title)}
                      style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "10px", flex: 1 }}
                    >
                      <div className="image-container">
                        <img src={item.thumbnail_url || MaxuLogo} alt={item.title} />
                      </div>
                      <div className="content-title">{item.title}</div>
                    </a>

                    <Tooltip title={saved ? t("contentCard.removeFromWatchlist") : t("contentCard.saveToWatchlist")}>
                      <div
                        className="content-save-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          saveHandler(item.title);
                        }}
                      >
                        <Icon sx={{ color: palette.orange[500], fontSize: "20px !important" }}>
                          {saved ? "bookmark" : "bookmark_border"}
                        </Icon>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {showIntroductoryMessage && (
        <div className="introductory-message">
          <IconButton onClick={closeIntroductionaryMessage} className="close-icon" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
          <p>{t("bot.welcome")}</p>
          <hr className="intro-hr" />
          <div className="intro-chat" onClick={toggleChatbot}>
            {t("bot.chatNow")}
          </div>
        </div>
      )}
      <div className="icon" onClick={toggleChatbot}>
        {showUnreadChat && <div className="unread-indicator">1</div>}
        <ChatBubbleOutlineOutlinedIcon />
      </div>
    </div>
  );
};

export default Bot;
